import React, { createContext, useCallback, useContext, useEffect, useState,} from "react";
import { adicionarFavorito,listarFavoritos,removerFavorito,} from "shared/services/api/favorito.api";
import { useAuth } from "shared/contexts/Auth/AuthContext";

/* ======================================
   TYPES
====================================== */

interface FavoritosContextData {
  favoritesIds: string[];

  addFavorite: (id: string) => Promise<void>;

  removeFavorite: (id: string) => Promise<void>;

  reloadFavorites: () => Promise<void>;
}

/* ======================================
   CONTEXT
====================================== */

const FavoritosContext = createContext({} as FavoritosContextData,);

/* ======================================
   PROVIDER
====================================== */

export const FavoritosProvider = ({children,}: {children: React.ReactNode;}) => {
  const { signed, loadingAuth } = useAuth();
  const [favoritesIds, setFavoritesIds] = useState<string[]>([]);

  /* ======================================
     CARREGAR FAVORITOS
  ====================================== */

  const carregarFavoritos = useCallback(async () => {
  if (!signed) {
    setFavoritesIds([]);
    return;
  }

  try {
    const favoritos = await listarFavoritos();

    setFavoritesIds(
      favoritos.map((produto: any) => produto.id)
    );
  } catch (error) {
    console.error("Erro ao carregar favoritos:", error);
    setFavoritesIds([]);
  }
}, [signed]);

  /* ======================================
     LOAD
  ====================================== */

  useEffect(() => {
  if (loadingAuth) {
    return;
  }

  if (!signed) {
    setFavoritesIds([]);
    return;
  }

  carregarFavoritos();
}, [signed, loadingAuth, carregarFavoritos]);

  /* ======================================
     ADICIONAR FAVORITO
  ====================================== */

  const addFavorite = async (id: string) => {
    try {
      await adicionarFavorito(id);

      setFavoritesIds((prev) => {
        if (prev.includes(id)) {
          return prev;
        }

        return [...prev, id];
      });

    } catch (error) {
      console.error("Erro ao adicionar favorito:", error);

      throw error;
    }
  };

  /* ======================================
     REMOVER FAVORITO
  ====================================== */

  const removeFavorite = async (id: string) => {
    try {
      await removerFavorito(id);

      setFavoritesIds((prev) =>
        prev.filter((item) => item !== id),
      );
    } catch (error) {
      console.error("Erro ao remover favorito:", error);

      throw error;
    }
  };

  /* ======================================
     PROVIDER
  ====================================== */

  return (
    <FavoritosContext.Provider
      value={{
        favoritesIds,
        addFavorite,
        removeFavorite,
        reloadFavorites: carregarFavoritos,
      }}
    >
      {children}
    </FavoritosContext.Provider>
  );
};

/* ======================================
   HOOK
====================================== */

export const useFavoritos = () =>
  useContext(FavoritosContext);